import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useCallback, useEffect } from 'react'
import { t } from 'i18next'

import {
  activeExportRequest,
  createExportRequest,
  getActiveExportRequests,
  clearExportRequestHistory,
  deleteExportRequest,
} from '_services/exports.service'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useWebSocket } from '@hapana/shared/web/components/shared/websocket/websocket.hook'
import { getErrorMessage } from '@hapana/shared/web/services/gqty'
import { useAuth } from '_hooks/auth.hooks'

const SOCKET_EVENTS = {
  ExportResponse: 'ExportResponse',
}

export function useCreateExportRequest(options = {}) {
  const { openToast } = useToast()
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()

  const onSuccess = () => {
    closeDialog()
    openToast({
      title: t('core:reports.exportRequest.successToast.title'),
      description: t('core:reports.exportRequest.successToast.description'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    queryClient.invalidateQueries({ queryKey: getActiveExportRequests.key })
  }

  const onError = (error) => {
    openToast({
      title: getErrorMessage(error, t('core:clients.errors.generic')),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
  }

  return useMutation(createExportRequest, { onSuccess, onError, ...options })
}

export function useActiveExportRequests(options = {}) {
  // DISABLED: Backend GraphQL schema not implemented yet
  // TODO: Re-enable when activeExportRequests query is added to backend schema
  return useQuery({
    queryFn: () => Promise.resolve({ activeExportRequests: [] }),
    queryKey: getActiveExportRequests.key,
    select: (data) => data.activeExportRequests,
    enabled: false, // Disable the query
    ...options,
  })

  // Original implementation (commented out):
  // return useQuery({
  //   queryFn: getActiveExportRequests.query,
  //   queryKey: getActiveExportRequests.key,
  //   select: (data) => data.activeExportRequests,
  //   ...options,
  // })
}

export function useClearExportRequestHistory(options = {}) {
  const { openToast } = useToast()
  const { closeDialog } = useDialog()

  const onSuccess = () => {
    closeDialog()
  }

  const onError = (error) => {
    openToast({
      title: getErrorMessage(error, t('core:clients.errors.generic')),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
  }

  return useMutation(clearExportRequestHistory, {
    onSuccess,
    onError,
    ...options,
  })
}

export function useExportRequest(options = {}) {
  const { openToast } = useToast()

  const onSuccess = (data) => {
    window.open(data.activeExportRequest.url, '_blank', 'noopener')
  }

  const onError = (error) => {
    openToast({
      title: getErrorMessage(error, t('core:clients.errors.generic')),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
  }

  return useMutation(activeExportRequest, { onSuccess, onError, ...options })
}

export function useDeleteExportRequest(options = {}) {
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: getActiveExportRequests.key })
  }

  const onError = (error) => {
    openToast({
      title: getErrorMessage(error, t('core:clients.errors.generic')),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
  }

  return useMutation(deleteExportRequest, { onSuccess, onError, ...options })
}

export function useExportReports() {
  const { setDialogState } = useDialog()

  const tenant = window.location.host.split('.')[0]
  const { session } = useAuth()
  const token = session?.idToken?.jwtToken

  const { socket, isConnected } = useWebSocket({
    tenant,
    token,
    authMechanism: 'core',
    userId: session?.idToken?.payload?.['custom:id'],
  })

  const queryClient = useQueryClient()

  const { openToast } = useToast()

  const handleDownloadReport = useCallback(
    (data) => {
      queryClient.invalidateQueries({ queryKey: getActiveExportRequests.key })

      setDialogState({
        latestExportRequest: data,
        hasRequestDownloadNotification: true,
      })
    },
    [setDialogState, queryClient]
  )

  useEffect(() => {
    if (isConnected) {
      socket?.on?.(SOCKET_EVENTS.ExportResponse, handleDownloadReport)
    }
  }, [handleDownloadReport, isConnected, socket, openToast])
}
